import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Input from '@components/forms/Input';
import Select from '@components/forms/Select';
import { Option } from '@material-tailwind/react';

import Button from '@components/forms/Button';
import { TableHeader } from '@components/widgets/VirtualTable';
import _isNil from 'lodash/isNil';
import { EmailStatusEnum, LenderStatusEnum, UserStatusEnum } from '@types';
import {
  post,
  LIST_LENDER_USERS,
  UPDATE_LENDER_USER,
  ADD_LENDER_USER,
  REMOVE_LENDER_USER
} from '@services';
import { useAuthStore, useLenderStore } from '@stores';
import {
  INVITATION_BOUNCED_LABEL,
  LENDER_STATUS_VARIANT
} from '@constants';
import StatusChip from '@components/widgets/StatusChip';
import { format, parseISO } from 'date-fns';
import { formatName } from '@utils/formatters';
import { isBouncedInvitation } from '@utils/userUtils';
import VirtualTable from '@components/widgets/VirtualTable';
import Modal from '@components/widgets/Modal';
import { ReactComponent as MoreVertIcon } from '@assets/icons/more-icon.svg';

import {
  IconButton,
  Menu,
  MenuHandler,
  MenuItem,
  MenuList
} from '@material-tailwind/react';

type SortDirection = 'asc' | 'desc';

// Define a basic interface if not available from store
interface LenderUser {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  lastLogin?: string | null;
  status: LenderStatusEnum;
  emailStatus?: EmailStatusEnum | null;
  organisationId: string;
  createdTime: string;
  isAdmin?: boolean;
  // Add other relevant fields if needed
}

const LandingTable = () => {
  const { selectedOrganisationId, getLender, isAdmin } = useAuthStore();
  const resetMFA = (row: any) => {
    setRow(row);
    setOpenMFA(true);
  };
  const changeRole = (row: any) => {
    setRow(row);
    setOpenRole(true);
  };
  const removeAccess = (row: any) => {
    setRow(row);
    setOpen(true);
  };
  const resendInvitation = (row: any) => {
    setRow(row);
    setOpenResend(true);
  };

  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        // Cycle through directions: asc -> desc -> none
        if (sortDirection === 'asc') {
          setSortDirection('desc');
        } else if (sortDirection === 'desc') {
          setSortKey(null); // Reset key to indicate unsorted state
          setSortDirection('asc'); // Reset direction for the next potential sort
        }
        // If sortKey is already null, clicking any header will enter the 'else' block
      } else {
        // Start sorting on a new column or re-sort after being unsorted
        setSortKey(key);
        setSortDirection('asc');
      }
    },
    [sortKey, sortDirection]
  );

  const TABLE_HEAD = useMemo(
    (): TableHeader[] => [
      {
        label: 'Username',
        field: 'email',
        contentClassName: 'text-grey-900 font-medium',
        onSort: handleSort
      },
      {
        label: 'Name',
        field: 'firstName',
        render: (row) =>
          formatName(row.firstName, row.lastName),
        onSort: handleSort
      },
      {
        label: 'Role',
        field: 'roles',
        render: (row) =>
          row.roles && row.roles.length > 0
            ? row.roles.includes('ADMIN')
              ? 'Admin'
              : 'Standard'
            : 'Standard',
        onSort: handleSort
      },
      {
        label: 'Last Login Date',
        field: 'lastLogin',
        render: (row) =>
          row.lastLogin && format(parseISO(row.lastLogin), 'dd/MM/yyyy h:mma'),
        onSort: handleSort
      },
      {
        label: 'Status',
        field: 'status',
        render: (row) => {
          const isBounced = isBouncedInvitation(row.status, row.emailStatus);

          return (
            <StatusChip
              variant={
                isBounced
                  ? 'error'
                  : LENDER_STATUS_VARIANT[row.status as LenderStatusEnum]
              }
              label={
                isBounced
                  ? INVITATION_BOUNCED_LABEL
                  : UserStatusEnum[row.status as keyof typeof UserStatusEnum]
              }
            />
          );
        },
        onSort: handleSort
      }
    ],
    [handleSort]
  );

  const [tableHeaders, setTableHeaders] = useState<TableHeader[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      const manageRoles = localStorage.getItem(`lenders_${userId}` as any);
      if (manageRoles) {
        if (JSON.parse(manageRoles) && JSON.parse(manageRoles).length > 0) {
          setRoles(JSON.parse(manageRoles)[0].roles);
        }
      }
    }
  }, []);
  const { query, status, setLenderList } = useLenderStore();

  // Store all data from API
  const [allUserData, setAllUserData] = useState<LenderUser[]>([]); // Store all data from API

  // Apply frontend filtering to all data
  const filteredData = useMemo(() => {
    let filtered = [...allUserData];

    // Apply keyword filtering
    let keyword = query;
    const pattern = /[`~!@#$^&*()={}':;',\\]/g;
    keyword = keyword.replace(pattern, '');

    if (keyword && keyword.trim() !== '') {
      const searchPattern = new RegExp(keyword.trim(), 'i');
      filtered = filtered.filter((item: LenderUser) => {
        return (
          (item.firstName && searchPattern.test(item.firstName)) ||
          (item.lastName && searchPattern.test(item.lastName)) ||
          (item.email && searchPattern.test(item.email))
        );
      });
    }

    // Apply status filtering
    if (status !== 'all') {
      filtered = filtered.filter(
        (item: LenderUser) => item.status.toLowerCase() === status.toLowerCase()
      );
    }

    return filtered;
  }, [allUserData, query, status]);

  const sortedData = useMemo(() => {
    // Use filtered data for sorting instead of lenderListData
    const dataToSort: LenderUser[] = filteredData || [];

    if (!sortKey || dataToSort.length === 0) {
      return dataToSort;
    }

    const sorted = [...dataToSort].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      if (sortKey === 'firstName') {
        // Combine firstName and lastName for sorting by Name
        aValue = formatName(a.firstName, a.lastName);
        bValue = formatName(b.firstName, b.lastName);
      } else if (sortKey === 'roles') {
        // Determine role string for sorting
        aValue = a.roles?.includes('ADMIN') ? 'Admin' : 'Standard';
        bValue = b.roles?.includes('ADMIN') ? 'Admin' : 'Standard';
      } else if (sortKey === 'lastLogin') {
        // Use timestamp for date sorting, handle nulls as past dates
        aValue = a.lastLogin ? parseISO(a.lastLogin).getTime() : 0;
        bValue = b.lastLogin ? parseISO(b.lastLogin).getTime() : 0;
      } else {
        aValue = a[sortKey as keyof LenderUser];
        bValue = b[sortKey as keyof LenderUser];
      }

      // Handle potential null/undefined values for string comparison
      aValue = _isNil(aValue) ? '' : aValue;
      bValue = _isNil(bValue) ? '' : bValue;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        return aValue - bValue;
      } else {
        // Fallback for other types or mixed types (basic comparison)
        if (aValue < bValue) return -1;
        if (aValue > bValue) return 1;
        return 0;
      }
    });

    return sortDirection === 'desc' ? sorted.reverse() : sorted;
  }, [filteredData, sortKey, sortDirection]);

  const [row, setRow] = useState<any>({});
  const [openResend, setOpenResend] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMFA, setOpenMFA] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showSaveMessage, setShowSaveMessage] = useState(false);
  const [showRemoveMessage, setShowRemoveMessage] = useState(false);
  const [showResendMessage, setShowResendMessage] = useState(false);
  const [orderThan, setOrderThan] = useState<string | null>(null);
  const onClose = () => {
    setOpen(false);
  };
  const onCloseMFA = () => {
    setOpenMFA(false);
  };
  const onCloseResend = () => {
    setOpenResend(false);
  };
  const onConfirm = () => {
    post(REMOVE_LENDER_USER, {
      lenderOrganisationId: selectedOrganisationId,
      userId: row.userId
    }).then(() => {
      setShowRemoveMessage(true);
      getLenderList();
      setTimeout(() => {
        setShowRemoveMessage(false);
        setOpen(false);
      }, 4000);
    });
  };
  const onConfirmResend = () => {
    post(ADD_LENDER_USER, {
      email: row.email,
      lenderOrganisationId: selectedOrganisationId
    }).then(() => {
      setShowResendMessage(true);
      getLenderList();
      setTimeout(() => {
        setShowResendMessage(false);
        setOpenResend(false);
      }, 4000);
    });
  };
  const onConfirmMFA = () => {
    post(UPDATE_LENDER_USER, {
      userId: row.userId,
      lenderOrganisationId: selectedOrganisationId,
      isMfaEnabled: false
    }).then(() => {
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        setOpenMFA(false);
      }, 4000);
    });
  };
  const onConfirmRole = useCallback(
    (role: any) => {
      post(UPDATE_LENDER_USER, {
        lenderOrganisationId: selectedOrganisationId,
        userId: row.userId,
        roles: [role]
      })
        .then(() => {
          setShowSaveMessage(true);
          getLenderList();
          setTimeout(() => {
            setShowSaveMessage(false);
            setOpenRole(false);
          }, 4000);
        })
        .catch((error) => {
          // Handle error
          console.error('Error updating user role:', error);
        });
    },
    [row]
  );
  function getLenderList() {
    const lender = getLender();
    if (!lender) {
      return;
    }
    const organisationId = selectedOrganisationId;
    post(LIST_LENDER_USERS, { organisationId, orderThan }).then((data) => {
      const newData = data.lenderUsers;

      if (newData.length > 0) {
        setOrderThan(newData[newData.length - 1].createdTime);
      }

      newData.forEach((e: any) => {
        e.isAdmin = lender.roles.includes('ADMIN');
      });

      // Store all raw data (no filtering here)
      setAllUserData(newData);
    });
  }
  useEffect(() => {
    const userId = localStorage.getItem('userId');

    if (isAdmin()) {
      setTableHeaders([
        ...TABLE_HEAD,
        {
          label: '',
          field: 'action',
          render: (row) => (
            <>
              {row.userId === userId ? null : (
                <Menu>
                  <MenuHandler>
                    <IconButton variant="text">
                      <MoreVertIcon />
                    </IconButton>
                  </MenuHandler>
                  <MenuList>
                    {row.status != LenderStatusEnum.Pending &&
                    row.status != LenderStatusEnum.InvitationExpired ? (
                      <MenuItem
                        onClick={() => resetMFA(row)}
                        className="font-medium text-grey-700 text-sm">
                        Reset MFA
                      </MenuItem>
                    ) : (
                      <MenuItem
                        onClick={() => resendInvitation(row)}
                        className="font-medium text-grey-700 text-sm">
                        Resend Invitation
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => changeRole(row)}
                      className="font-medium text-grey-700 text-sm">
                      Change role
                    </MenuItem>
                    <MenuItem
                      onClick={() => removeAccess(row)}
                      className="font-medium text-rose-700 text-sm">
                      Remove access
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </>
          )
        }
      ]);
    } else {
      setTableHeaders(TABLE_HEAD);
    }
  }, [roles, TABLE_HEAD]);

  useEffect(() => {
    getLenderList();
  }, []); // Only run on mount, not on query/status changes

  // Update store when paginated data changes
  useEffect(() => {
    setLenderList(sortedData);
  }, [sortedData, setLenderList]);

  const [openRole, setOpenRole] = useState(false);
  const onCloseRole = () => {
    setOpenRole(false);
  };

  return (
    <div className="pb-12">
      <VirtualTable
        headers={tableHeaders}
        data={sortedData}
        sortKey={sortKey}
        sortDirection={sortDirection}
        itemHeight={64}
      />

      <ResetMFAModal
        open={openMFA}
        row={row}
        showSuccessMessage={showSuccessMessage}
        onClose={onCloseMFA}
        onConfirm={onConfirmMFA}
      />
      <RemoveAccessModal
        open={open}
        row={row}
        onClose={onClose}
        showSuccessMessage={showRemoveMessage}
        onConfirm={onConfirm}
      />
      <ChangeRoleModal
        open={openRole}
        row={row}
        showSuccessMessage={showSaveMessage}
        onClose={onCloseRole}
        onConfirm={(role: any) => onConfirmRole(role)}
      />
      <ResendInvitationModal
        open={openResend}
        row={row}
        showSuccessMessage={showResendMessage}
        onClose={onCloseResend}
        onConfirm={() => onConfirmResend()}
      />
    </div>
  );
};
const RemoveAccessModal = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}: {
  open: boolean;
  row: any;
  onClose: any;
  onConfirm: any;
  showSuccessMessage: boolean;
}) => {
  return (
    <Modal title="Remove Access" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-[10px]">
        <div className="flex flex-col gap-[10px] text-[#475467]">
          <p>
            Are you sure you want to remove access for{' '}
            <span className="font-medium font-bold text-[#000000]">
              {row?.email}
            </span>{' '}
            from this account?
          </p>
        </div>
        <div className="mt-2">
          <div className="h-4 w-full text-right mb-4">
            {showSuccessMessage && (
              <div className="mt-4 text-green-500">Access removed!</div>
            )}
          </div>
          <div className="grid grid-cols-2 w-full gap-4 ">
            <Button
              variant="outlined"
              data-dialog-close="true"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-600 normal-case font-semibold rounded-lg text-base w-auto focus:ring focus:ring-white">
              Cancel
            </Button>
            <Button
              data-dialog-close="true"
              className="font-semibold bg-primary normal-case rounded-lg text-base text-white w-auto whitespace-nowrap"
              onClick={onConfirm}>
              Remove Access
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ChangeRoleModal = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}: {
  open: boolean;
  showSuccessMessage: boolean;
  row: any;
  onClose: any;
  onConfirm: any;
}) => {
  const role = row?.roles?.includes('ADMIN') ? 'ADMIN' : 'STANDARD';
  const email = row?.email || '';
  const [selectedRole, setSelectedRole] = useState(role);

  useEffect(() => {
    setSelectedRole(role);
  }, [role]);
  const handleRoleChange = (e: any) => {
    setSelectedRole(e);
  };
  return (
    <Modal title="Change User's Role" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-[10px]">
        <div className="mt-4">
          <p className="text-sm font-medium text-grey-700 mb-1.5">Email</p>
          <Input disabled placeholder="Email" type="email" value={email} />
        </div>
        <div className="mt-4">
          <p className="text-sm font-medium text-grey-700 mb-1.5">Role</p>
          <Select
            className="!h-10 lg:flex items-center text-sm leading-6 text-grey-400 rounded-md ring-1 ring-grey-900/10 shadow-sm hover:ring-grey-30 dark:bg-grey-800 dark:highlight-white/5 dark:hover:bg-grey-700"
            value={selectedRole}
            onChange={handleRoleChange}
            selected={(element: any) =>
              element &&
              React.cloneElement(element, {
                className: 'flex items-center px-0 gap-2 pointer-events-none'
              })
            }>
            <Option
              key="admin"
              className="flex items-center gap-2"
              value={'ADMIN'}>
              <div>Admin</div>
            </Option>
            <Option
              key="standard"
              className="flex items-center gap-2"
              value={'STANDARD'}>
              <div>Standard</div>
            </Option>
          </Select>
        </div>
        <div className="mt-2">
          <div className="h-4 w-full text-right mb-4">
            {showSuccessMessage && (
              <div className="mt-4 text-green-500">Changes saved!</div>
            )}
          </div>
          <div className="flex gap-4 justify-center lg:justify-end">
            <Button
              variant="outlined"
              data-dialog-close="true"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-600 normal-case font-semibold rounded-lg text-base w-auto focus:ring focus:ring-white">
              Cancel
            </Button>
            <Button
              data-dialog-close="true"
              className="font-semibold bg-primary normal-case rounded-lg text-base text-white w-auto whitespace-nowrap"
              onClick={() => onConfirm(selectedRole)}>
              Save
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ResendInvitationModal = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}: {
  open: boolean;
  showSuccessMessage: boolean;
  row: any;
  onClose: any;
  onConfirm: any;
}) => {
  const email = row?.email || '';

  return (
    <Modal title="Resend Invitation" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-[10px]">
        <div className="mt-4">
          <p className="text-sm font-medium text-grey-700 mb-1.5">Email</p>
          <Input disabled placeholder="Email" type="email" value={email} />
        </div>

        <div className="mt-2">
          <div className="h-4 w-full text-right mb-4">
            {showSuccessMessage && (
              <div className="mt-4 text-green-500">Invite sent!</div>
            )}
          </div>
          <div className="flex gap-4 justify-center lg:justify-end">
            <Button
              variant="outlined"
              data-dialog-close="true"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-600 normal-case font-semibold rounded-lg text-base w-auto focus:ring focus:ring-white">
              Cancel
            </Button>
            <Button
              data-dialog-close="true"
              className="font-semibold bg-primary normal-case rounded-lg text-base text-white w-auto whitespace-nowrap"
              onClick={() => onConfirm()}>
              Resend Invite
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const ResetMFAModal = ({
  open,
  row,
  onClose,
  showSuccessMessage,
  onConfirm
}: {
  open: boolean;
  showSuccessMessage: boolean;
  row: any;
  onClose: any;
  onConfirm: any;
}) => {
  return (
    <Modal title="Reset MFA" size="md" open={open} onClose={onClose}>
      <div className="flex flex-col gap-[10px]">
        <div className="flex flex-col gap-[10px] text-[#475467]">
          <p>
            Resetting the Multi-Factor Authentication (MFA) for{' '}
            <span className="font-medium font-bold text-[#000000]">
              {row?.email}
            </span>{' '}
            will require this user to set up their MFA method the next time they
            log in successfully.
          </p>
          <p className="mt-4">Are you sure you want to proceed?</p>
        </div>
        <div className="mt-2">
          <div className="h-4 w-full text-right mb-4">
            {showSuccessMessage && (
              <div className="mt-4 text-green-500">MFA Reset!</div>
            )}
          </div>
          <div className="grid grid-cols-2 w-full gap-4 ">
            <Button
              variant="outlined"
              data-dialog-close="true"
              onClick={onClose}
              className="border bg-white border-grey-300 text-grey-600 normal-case font-semibold rounded-lg text-base w-auto focus:ring focus:ring-white">
              Cancel
            </Button>
            <Button
              data-dialog-close="true"
              className="font-semibold bg-primary normal-case rounded-lg text-base text-white w-auto whitespace-nowrap"
              onClick={onConfirm}>
              Reset MFA
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
export default LandingTable;
